'use client';

import { Suspense, useState, useMemo } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button, Input } from '@boty/ui';
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  Skeleton,
  Paper,
} from '@mui/material';
import { useLocale, useTranslations } from 'next-intl';

type LoginFormData = {
  email: string;
  password: string;
};

interface LoginResponse {
  data?: {
    requiresTwoFactor: boolean;
    tempToken?: string;
    expiresAt?: string;
    user?: { id: string; email: string; role: string };
    session?: unknown;
    warning?: string;
  };
  error?: {
    code: string;
    message: string;
  };
}

function AdminLoginForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const redirectTo = searchParams.get('redirectTo');
  const locale = useLocale();
  const t = useTranslations('admin.auth');
  const [error, setError] = useState<string | null>(null);

  const loginSchema = useMemo(
    () =>
      z.object({
        email: z.string().email('Please enter a valid email address'),
        password: z.string().min(1, 'Password is required'),
      }),
    []
  );

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormData) => {
    setError(null);

    try {
      const response = await fetch('/api/admin/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      const result: LoginResponse = await response.json();

      if (result.error) {
        switch (result.error.code) {
          case 'ACCOUNT_LOCKED':
            setError(t('accountLocked'));
            break;
          case 'IP_BLOCKED':
            setError(t('ipNotAllowed'));
            break;
          case 'AUTH_FAILED':
            setError(t('invalidCredentials'));
            break;
          default:
            setError(result.error.message);
        }
        return;
      }

      if (result.data?.requiresTwoFactor && result.data.tempToken) {
        // Store temp token and redirect to 2FA page
        sessionStorage.setItem('admin_2fa_token', result.data.tempToken);
        const twoFaUrl = `/${locale}/2fa${redirectTo ? `?redirectTo=${encodeURIComponent(redirectTo)}` : ''}`;
        router.push(twoFaUrl);
        return;
      }

      // Direct login (2FA not enabled) - show warning but allow
      if (result.data?.warning) {
        console.warn('[Admin Login]', result.data.warning);
      }

      // Redirect to dashboard or requested page
      const destination = redirectTo || `/${locale}/dashboard`;
      router.push(destination);
    } catch (err) {
      console.error('[Admin Login] Error:', err);
      setError('An unexpected error occurred. Please try again.');
    }
  };

  return (
    <>
      {error && (
        <Alert severity="error" sx={{ mb: 2, width: '100%' }}>
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ width: '100%' }}>
        <Input
          label={t('email')}
          type="email"
          fullWidth
          autoComplete="email"
          autoFocus
          error={!!errors.email}
          helperText={errors.email?.message}
          {...register('email')}
          sx={{ mb: 2 }}
        />

        <Input
          label={t('password')}
          type="password"
          fullWidth
          autoComplete="current-password"
          error={!!errors.password}
          helperText={errors.password?.message}
          {...register('password')}
          sx={{ mb: 3 }}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={isSubmitting}
          size="large"
        >
          {isSubmitting ? <CircularProgress size={24} /> : t('signIn')}
        </Button>
      </Box>
    </>
  );
}

function LoginFormSkeleton() {
  return (
    <Box sx={{ width: '100%' }}>
      <Skeleton variant="rectangular" height={56} sx={{ mb: 2 }} />
      <Skeleton variant="rectangular" height={56} sx={{ mb: 3 }} />
      <Skeleton variant="rectangular" height={48} />
    </Box>
  );
}

export default function AdminLoginPage() {
  const t = useTranslations('admin.auth');

  return (
    <Paper
      elevation={3}
      sx={{
        p: 4,
        width: '100%',
        maxWidth: 400,
        borderRadius: 2,
      }}
    >
      <Typography variant="h4" component="h1" gutterBottom align="center" fontWeight="bold">
        {t('loginTitle')}
      </Typography>

      <Typography
        variant="body2"
        color="text.secondary"
        align="center"
        sx={{ mb: 3 }}
      >
        {t('loginSubtitle')}
      </Typography>

      <Suspense fallback={<LoginFormSkeleton />}>
        <AdminLoginForm />
      </Suspense>
    </Paper>
  );
}
